import {
  type KK,
  type DefineDevice,
  type SetMagnetSenior,
  KIO,
  Record,
  OutputMode,
  type Output,
  VirtualHE3SEWireless,
  VirtualHE3SEWireless_24g,
  VirtualMars68ProWireless,
  VirtualMars68ProWireless_24g,
  VirtualMars68Wireless,
  VirtualMg68Max_24g,
  VirtualMg68Max,
  VirtualMars68Wireless_24g,
  VirtualHe6Ultra,
  VirtualHe6Ultra_24g,
  VirtualMG68ACE_W,
  VirtualMG68ACE_W_24g,
  VirtualIrokFe87V2IMC,
  VirtualIrokFe87V2IMC_24g,
  VirtualIrokFe87V2,
  VirtualIrokFe87V2_24g,
  VK,
} from '../define/define'
import type { DataSetsList, DataSetLink } from '../define/response'
import type { PostString, PostInt, PostCfgRename, PostImportSetting, PostImportReplace, PostCfgBindShortCut } from '../define/request'
import { deviceManager } from '../device/device_manager'
import { DataDeadZone, DataTravel, KeysGroup, PostSetLed, DataProfileForDecorate, PostDynamicColor } from '@libs/types/response'
import { createProfile } from '@hid/define/define_profile'
import { SpecificItem } from '@libs/types/enum'
import { InternalActionKeyId } from '@/define/internal_action_keys'

const onboardSwitchActionSlotMap = new Map<VK, number>([
  [InternalActionKeyId.deviceSwitchToProfile1 as VK, 0],
  [InternalActionKeyId.deviceSwitchToProfile2 as VK, 1],
  [InternalActionKeyId.deviceSwitchToProfile3 as VK, 2],
  [InternalActionKeyId.deviceSwitchToProfile4 as VK, 3],
])

const getOnboardSwitchActionIdBySlot = (slot: number) => {
  for (const [actionId, targetSlot] of onboardSwitchActionSlotMap.entries()) {
    if (targetSlot === slot) {
      return actionId
    }
  }

  return undefined
}

export interface MouseProfile {
  DpiColors: number[][]
  DpiIndex: number // 表示当前 DPI 索引
  DpiList: number[] // 表示 DPI 列表，最大长度为7
  Rate: number // 表示鼠标报告率
}

export interface LightModeSetting {
  Light: number
  Speed: number
}

export type LightModeSettingMap = { [mode: string]: LightModeSetting }

export interface DataProfile {
  LightLastIsCustom: boolean
  Keys: KIO[]
  Macros: Map<number, Record[]>
  LightMode: PostSetLed
  LightModeSettings?: LightModeSettingMap
  ColorsCustom: Map<KK, number>
  ColorsDynamic: PostDynamicColor
  Mouse: MouseProfile
  MagnetData: Map<number, SetMagnetSenior>
  MagnetTravel: Map<KK, DataTravel>
  //KeysGroup: Map<KK, KeyGroup>;
  KeysGroup: KeysGroup[]
  DeadZone: number // 废弃
  DeadZone1: [number, number] // 废弃
  DeadZoneList: Map<KK, DataDeadZone> //只有切换配置的时候, 会使用
  Items: Map<SpecificItem, number>
  Decorates?: DataProfileForDecorate[] // 灯光装饰颜色配置
}
export interface ProfileMetadata {
  Name: string // 配置名称
  Shortcut?: VK[] // 绑定的快捷键列表
  ShortcutRet?: boolean // 快捷键注册状态
  slotIndex: number | null // 板载槽位索引 (0-3)，null 表示存档配置
  OnboardSlot?: number | null // @deprecated 兼容旧数据迁移
  OnboardSlotMap?: { [deviceKey: string]: number | null } // @deprecated 兼容旧数据迁移
  ProfilesMap: Map<DefineDevice, DataProfile> // 设备配置映射
}

export interface ProfileList {
  CurrentProfileIndex: number // 当前配置索引
  Profiles: ProfileMetadata[] // 配置列表
}

type OnboardNameStore = { [deviceKey: string]: { [slotKey: string]: string } }

const getLightModeSettingKey = (mode: number) => String(mode)

export const ensureLightModeSettings = (profile: DataProfile) => {
  if (!profile.LightModeSettings) {
    profile.LightModeSettings = {}
  }

  if (typeof profile.LightMode?.Mode !== 'number') {
    return profile.LightModeSettings
  }

  const modeKey = getLightModeSettingKey(profile.LightMode.Mode)
  if (!profile.LightModeSettings[modeKey]) {
    profile.LightModeSettings[modeKey] = {
      Light: profile.LightMode.Light,
      Speed: profile.LightMode.Speed,
    }
  }

  return profile.LightModeSettings
}

export const syncLightModeSetting = (profile: DataProfile, mode: number, lightMode: Pick<PostSetLed, 'Light' | 'Speed'>) => {
  const settings = ensureLightModeSettings(profile)
  settings[getLightModeSettingKey(mode)] = {
    Light: lightMode.Light,
    Speed: lightMode.Speed,
  }
  return settings[getLightModeSettingKey(mode)]
}

export const resolveLightModeSetting = (profile: DataProfile, lightMode: PostSetLed) => {
  const settings = ensureLightModeSettings(profile)
  const currentSetting = settings[getLightModeSettingKey(lightMode.Mode)]

  if (!currentSetting) {
    syncLightModeSetting(profile, lightMode.Mode, lightMode)
    return lightMode
  }

  return {
    ...lightMode,
    Light: currentSetting.Light,
    Speed: currentSetting.Speed,
  }
}

// 转换 Map 为普通对象
export function mapToObject(map: Map<any, any>): { [key: string]: any } {
  const obj: { [key: string]: any } = {}
  if (map instanceof Map) {
    map.forEach((value, key) => {
      obj[key] = value
    })
  } else {
    console.error('Input is not a Map object.')
  }
  return obj
}

// 转换普通对象为 Map
function objectToMap(obj: { [key: string]: any }): Map<any, any> {
  const map = new Map()
  if (typeof obj === 'object' && obj !== null) {
    for (const key in obj) {
      if (!isNaN(Number(key))) {
        const k = Number(key)
        map.set(k, obj[k])
      } else {
        map.set(key, obj[key])
      }
    }
  } else {
    console.error('Input is not a plain object.')
  }
  return map
}

interface DeviceMap {
  wired: DefineDevice
  wireless: DefineDevice
}

// 示例设备映射
const deviceMapping: DeviceMap[] = [
  { wired: VirtualHE3SEWireless, wireless: VirtualHE3SEWireless_24g },
  { wired: VirtualMars68ProWireless, wireless: VirtualMars68ProWireless_24g },
  { wired: VirtualMars68Wireless, wireless: VirtualMars68Wireless_24g },
  { wired: VirtualMg68Max, wireless: VirtualMg68Max_24g },
  { wired: VirtualHe6Ultra, wireless: VirtualHe6Ultra_24g },
  { wired: VirtualMG68ACE_W, wireless: VirtualMG68ACE_W_24g },
  { wired: VirtualIrokFe87V2IMC, wireless: VirtualIrokFe87V2IMC_24g },
  { wired: VirtualIrokFe87V2, wireless: VirtualIrokFe87V2_24g },
]

function getAnotherDevice(defineDevice: DefineDevice): DefineDevice | null {
  for (const device of deviceMapping) {
    if (device.wired === defineDevice) {
      return device.wireless
    } else if (device.wireless === defineDevice) {
      return device.wired
    }
  }
  return null
}
export class DataProfileManage {
  public _dataProfile: DataProfile = {} as DataProfile
  private profileList: ProfileList = {} as ProfileList

  // 板载首次整包同步完成标记：仅存内存，不持久化到 localStorage。
  // 用户刷新网页后该状态清空，从而可以重新执行首次同步检测。
  // 按设备组 key 区分，保证会话内切换设备时各自独立。
  private onboardInitialSyncDoneSet = new Set<string>()

  /**
   * 获取当前设备的设备组 key。
   * 配对的有线/无线设备共用同一个 key（取较小 ID）。
   */
  private getDeviceGroupKey(): string {
    const currentId = deviceManager.deviceID
    const anotherId = getAnotherDevice(currentId)
    if (anotherId !== null) {
      return String(Math.min(currentId, anotherId))
    }
    return String(currentId)
  }

  private get profileKey(): string {
    return `dataProfileList_${this.getDeviceGroupKey()}`
  }

  private get onboardNameKey(): string {
    return `onboardConfigNameMap_${this.getDeviceGroupKey()}`
  }

  private getRelatedDeviceKeys() {
    const deviceKeys = [String(deviceManager.deviceID)]
    const anotherDevice = getAnotherDevice(deviceManager.deviceID)
    if (anotherDevice !== null) {
      deviceKeys.push(String(anotherDevice))
    }
    return deviceKeys
  }

  private resolveLegacySlotIndex(profile: Pick<ProfileMetadata, 'slotIndex' | 'OnboardSlot' | 'OnboardSlotMap'>): number | null {
    if (typeof profile.slotIndex === 'number') return profile.slotIndex

    const slotMap = profile.OnboardSlotMap || {}
    for (const deviceKey of this.getRelatedDeviceKeys()) {
      const slot = slotMap[deviceKey]
      if (typeof slot === 'number') {
        return slot
      }
    }

    if (typeof profile.OnboardSlot === 'number') {
      return profile.OnboardSlot
    }

    return null
  }

  /**
   * 迁移旧数据：将 OnboardSlot/OnboardSlotMap 转换为 slotIndex
   */
  private migrateProfileSlotIndex(profile: ProfileMetadata) {
    profile.slotIndex = this.resolveLegacySlotIndex(profile)
  }

  private loadOnboardNameStore(): OnboardNameStore {
    try {
      const value = localStorage.getItem(this.onboardNameKey)
      if (!value) {
        return {}
      }
      return JSON.parse(value)
    } catch {
      return {}
    }
  }

  private saveOnboardNameStore(store: OnboardNameStore) {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(this.onboardNameKey)
      return
    }
    localStorage.setItem(this.onboardNameKey, JSON.stringify(store))
  }

  public get dataProfile() {
    const currentProfile = this.profileList.Profiles[this.profileList.CurrentProfileIndex]
    const profile = currentProfile.ProfilesMap.get(deviceManager.deviceID)
    if (profile) {
      this._dataProfile = profile
      ensureLightModeSettings(this._dataProfile)
      return this._dataProfile
    } else {
      this.createDefaultProfile()
      return this._dataProfile
    }
    //throw new Error("dataProfile is undefined");
  }
  public set dataProfile(dataProfile: DataProfile) {
    this._dataProfile = dataProfile
    ensureLightModeSettings(this._dataProfile)
  }

  constructor() {
    this.load()
  }

  /**
   * 切换设备后重新加载配置列表。
   * profileKey 是动态计算的，load() 会自动读取新设备组的存储。
   */
  public reload() {
    this.load()
  }

  /**
   * 一次性迁移：将旧的全局 'dataProfileList' 拆分到当前设备组的独立 key。
   * 只迁移当前设备组的数据，其他设备组在它们首次连接时再迁移。
   * 迁移后不删除旧数据（作为备份）。
   */
  private migrateFromGlobalStorage() {
    const GLOBAL_KEY = 'dataProfileList'

    // 当前设备组的 key 已存在，无需迁移
    if (localStorage.getItem(this.profileKey)) return

    const globalData = localStorage.getItem(GLOBAL_KEY)
    if (!globalData) return

    try {
      const parsed = JSON.parse(globalData)
      if (!parsed.Profiles || !Array.isArray(parsed.Profiles)) return

      // 收集当前设备组包含的所有设备 ID
      const currentDeviceIds = new Set<number>()
      currentDeviceIds.add(deviceManager.deviceID)
      const another = getAnotherDevice(deviceManager.deviceID)
      if (another !== null) {
        currentDeviceIds.add(another)
      }

      // 只提取属于当前设备组的 ProfilesMap 条目
      const groupProfiles = parsed.Profiles.map((profile: any, originalIndex: number) => {
        const filteredMap: any = {}
        for (const deviceId of currentDeviceIds) {
          if (profile.ProfilesMap && profile.ProfilesMap[String(deviceId)]) {
            filteredMap[String(deviceId)] = profile.ProfilesMap[String(deviceId)]
          }
        }
        const slotIndex = this.resolveLegacySlotIndex({
          slotIndex: profile.slotIndex,
          OnboardSlot: profile.OnboardSlot,
          OnboardSlotMap: profile.OnboardSlotMap,
        })
        return {
          originalIndex,
          Name: profile.Name,
          Shortcut: profile.Shortcut || [],
          ShortcutRet: profile.ShortcutRet || false,
          slotIndex,
          OnboardSlot: profile.OnboardSlot,
          OnboardSlotMap: profile.OnboardSlotMap,
          ProfilesMap: filteredMap,
        }
      })

      // 仅迁移当前设备组实际使用过的配置，避免新设备继承其他设备的配置外壳。
      // 若当前设备组没有任何旧数据，则至少保留第一条配置，随后由 createDefaultProfile() 补齐设备数据。
      const nonEmptyProfiles = groupProfiles.filter((profile: any) => Object.keys(profile.ProfilesMap).length > 0)
      const finalProfiles = nonEmptyProfiles.length > 0 ? nonEmptyProfiles : groupProfiles.slice(0, 1)
      const currentProfileIndex = Math.max(
        0,
        finalProfiles.findIndex((profile: any) => profile.originalIndex === (parsed.CurrentProfileIndex ?? 0)),
      )
      const groupData = {
        CurrentProfileIndex: currentProfileIndex,
        Profiles: finalProfiles.map(({ originalIndex: _originalIndex, ...profile }: any) => profile),
      }

      localStorage.setItem(this.profileKey, JSON.stringify(groupData))
    } catch (e) {
      console.error('[Profile Migration] Failed to migrate profile data for device group:', e)
    }
  }

  // 存储 DataProfile 到 localStorage
  public save() {
    //处理一个设备对应两个配置
    const anotherDevice = getAnotherDevice(deviceManager.deviceID)
    if (anotherDevice) {
      this.profileList.Profiles[this.profileList.CurrentProfileIndex].ProfilesMap.set(anotherDevice, this.dataProfile)
    }
    const profiles = this.profileList.Profiles.map((profile) => {
      const profilesMapObj: { [key: string]: any } = {}
      profile.ProfilesMap.forEach((value, key) => {
        profilesMapObj[String(key)] = {
          ...value,
          Macros: value.Macros,
          LightModeSettings: ensureLightModeSettings(value),
          ColorsCustom: value.ColorsCustom,
          MagnetData: value.MagnetData,
          MagnetTravel: value.MagnetTravel,
          DeadZoneList: value.DeadZoneList,
          Items: value.Items,
          Decorates: value.Decorates,
        }
      })
      return {
        Name: profile.Name,
        Shortcut: profile.Shortcut,
        ShortcutRet: profile.ShortcutRet,
        slotIndex: profile.slotIndex ?? null,
        ProfilesMap: profilesMapObj,
      }
    })
    const profileListObj = {
      CurrentProfileIndex: this.profileList.CurrentProfileIndex,
      Profiles: profiles,
    }

    const profileString = JSON.stringify(profileListObj)
    // console.log("save", profileString)
    localStorage.setItem(this.profileKey, profileString)
  }

  // 从 localStorage 读取 DataProfile
  private load() {
    this.migrateFromGlobalStorage()

    const profileString = localStorage.getItem(this.profileKey)
    if (!profileString) {
      this.createDefaultListAndProfile()
      return
    }
    const profileListObj = JSON.parse(profileString)
    this.profileList = {
      CurrentProfileIndex: profileListObj.CurrentProfileIndex,
      Profiles: profileListObj.Profiles.map(
        (profile: { Name: string; Shortcut?: VK[]; ShortcutRet?: boolean; slotIndex?: number | null; OnboardSlot?: number | null; OnboardSlotMap?: { [deviceKey: string]: number | null }; ProfilesMap: { [key: string]: any } }) => {
          const profilesMap = new Map<DefineDevice, DataProfile>()
          for (const key in profile.ProfilesMap) {
            const value = profile.ProfilesMap[key]
            profilesMap.set(Number(key) as DefineDevice, {
              ...value,
              Macros: objectToMap(value.Macros),
              LightModeSettings: value.LightModeSettings,
              ColorsCustom: objectToMap(value.ColorsCustom),
              MagnetData: objectToMap(value.MagnetData),
              MagnetTravel: objectToMap(value.MagnetTravel),
              DeadZoneList: objectToMap(value.DeadZoneList),
              Items: objectToMap(value.Items),
              Decorates:
                value.Decorates && Array.isArray(value.Decorates)
                  ? value.Decorates.map((decorate: any) => ({
                      ...decorate,
                      ColorsCustom: objectToMap(decorate.ColorsCustom),
                    }))
                  : undefined,
            })
          }
          const metadata: ProfileMetadata = {
            Name: profile.Name,
            Shortcut: profile.Shortcut || [],
            ShortcutRet: profile.ShortcutRet || false,
            slotIndex: profile.slotIndex ?? null,
            OnboardSlot: profile.OnboardSlot,
            OnboardSlotMap: profile.OnboardSlotMap,
            ProfilesMap: profilesMap,
          }
          this.migrateProfileSlotIndex(metadata)
          return metadata
        },
      ),
    }

    if (this.profileList.CurrentProfileIndex >= this.profileList.Profiles.length) {
      throw new Error('Current profile index out of range')
    }
    const currentProfile = this.profileList.Profiles[this.profileList.CurrentProfileIndex]
    if (currentProfile.ProfilesMap.has(deviceManager.deviceID)) {
      this.dataProfile = currentProfile.ProfilesMap.get(deviceManager.deviceID) as DataProfile
    } else {
      this.createDefaultProfile()
    }
  }

  // 创建配置列表和当前设备的配置
  private createDefaultListAndProfile() {
    this.profileList = {
      CurrentProfileIndex: 0,
      Profiles: [{ Name: '默认配置', slotIndex: 0, ProfilesMap: new Map<DefineDevice, DataProfile>() }],
    }
    this.createDefaultProfile()
  }

  // 创建当前设备的配置
  private createDefaultProfile() {
    this._dataProfile = createProfile(deviceManager.deviceID)
    ensureLightModeSettings(this._dataProfile)
    this.profileList.Profiles[this.profileList.CurrentProfileIndex].ProfilesMap.set(deviceManager.deviceID, this._dataProfile)
    this.save()
  }

  // 新增配置构建返回值
  private buildCfgCommonReturn(): DataSetsList {
    const dataSetList: DataSetLink[] = this.profileList.Profiles.map((profile) => ({
      Name: profile.Name,
      File: ``,
      ShortcutRet: profile.ShortcutRet || false,
      Shortcut: profile.Shortcut || [],
      slotIndex: profile.slotIndex,
      OnboardSlot: profile.slotIndex,
      pendingInitialSync: profile.slotIndex != null ? this.isSlotPendingInitialSync(profile.slotIndex) : false,
    }))
    return {
      Current: this.profileList.CurrentProfileIndex,
      List: dataSetList,
    }
  }

  // 实现绑定快捷键的数据
  public CfgBindShortcut(post: PostCfgBindShortCut) {
    if (post.ID < 0 || post.ID >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    this.profileList.Profiles[post.ID].Shortcut = post.Shortcut
    // 假设绑定即成功，或者根据实际需求处理状态
    this.profileList.Profiles[post.ID].ShortcutRet = post.Shortcut && post.Shortcut.length > 0
    this.save()
    return this.buildCfgCommonReturn()
  }

  // 新增配置（新配置默认为存档）
  public CfgCreate(post: PostString) {
    this.assertCanAddProfile()
    this.profileList.Profiles.push({ Name: post.Value, slotIndex: null, ProfilesMap: new Map<DefineDevice, DataProfile>() })
    this.save()
    return this.buildCfgCommonReturn()
  }

  private getMaxProfileCount() {
    // 支持板载的设备限制 8 个；不支持板载的设备无上限
    return deviceManager.deviceDriver.supportsOnboardConfigs() ? 8 : Infinity
  }

  private assertCanAddProfile() {
    const maxProfileCount = this.getMaxProfileCount()
    if (this.profileList.Profiles.length >= maxProfileCount) {
      throw new Error(`profile count limit exceeded: ${maxProfileCount}`)
    }
  }

  //配置列表
  public CfgList() {
    return this.buildCfgCommonReturn()
  }

  //删除配置
  public CfgDelete(post: PostInt) {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    if (this.profileList.CurrentProfileIndex === post.Value) {
      throw new Error('can not delete current profile')
    }
    if (this.profileList.Profiles.length === 1) {
      throw new Error('can not delete last profile')
    }
    // 仅板载设备：只允许删除存档配置（slotIndex === null）
    if (deviceManager.deviceDriver.supportsOnboardConfigs()) {
      const deletingProfile = this.profileList.Profiles[post.Value]
      if (deletingProfile && deletingProfile.slotIndex !== null) {
        throw new Error('can not delete active slot config, archive it first')
      }
    }

    this.profileList.Profiles.splice(post.Value, 1)
    if (this.profileList.CurrentProfileIndex > post.Value) {
      this.profileList.CurrentProfileIndex--
    }
    this.save()

    return this.buildCfgCommonReturn()
  }

  //配置重命名
  public CfgRename(post: PostCfgRename) {
    if (post.ID < 0 || post.ID >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    this.profileList.Profiles[post.ID].Name = post.Name
    this.save()

    return this.buildCfgCommonReturn()
  }

  //配置切换
  public CfgSwitch(post: PostInt) {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    if (this.profileList.CurrentProfileIndex === post.Value) {
      return this.buildCfgCommonReturn()
    }
    this.profileList.CurrentProfileIndex = post.Value
    const dataProfile = this.profileList.Profiles[post.Value].ProfilesMap.get(deviceManager.deviceID)
    if (dataProfile) {
      this.dataProfile = dataProfile
      this.save()
    } else {
      this.createDefaultProfile()
    }
    return this.buildCfgCommonReturn()
  }

  /**
   * 根据槽位索引通过 slotIndex 直接对齐 CurrentProfileIndex
   */
  public alignCurrentProfileToSlot(slot: number) {
    if (slot < 0) {
      return this.buildCfgCommonReturn()
    }

    const profileIndex = this.getProfileIndexBySlot(slot)
    if (profileIndex === null || this.profileList.CurrentProfileIndex === profileIndex) {
      return this.buildCfgCommonReturn()
    }

    this.profileList.CurrentProfileIndex = profileIndex
    const dataProfile = this.profileList.Profiles[profileIndex].ProfilesMap.get(deviceManager.deviceID)
    if (dataProfile) {
      this.dataProfile = dataProfile
    } else {
      this.createDefaultProfile()
    }
    this.save()

    return this.buildCfgCommonReturn()
  }

  public getCurrentProfileName() {
    return this.profileList.Profiles[this.profileList.CurrentProfileIndex]?.Name || ''
  }

  public getCurrentProfileSlotIndex(): number | null {
    return this.profileList.Profiles[this.profileList.CurrentProfileIndex]?.slotIndex ?? null
  }

  /**
   * 根据 profile index 获取其 slotIndex
   */
  public getSlotByProfileIndex(profileIndex: number): number | null {
    if (profileIndex < 0 || profileIndex >= this.profileList.Profiles.length) return null
    return this.profileList.Profiles[profileIndex].slotIndex ?? null
  }

  public getProfileIndexBySlot(slot: number): number | null {
    const index = this.profileList.Profiles.findIndex((profile) => profile.slotIndex === slot)
    return index >= 0 ? index : null
  }

  /**
   * 获取所有活跃配置（有 slotIndex 0-3 的）
   */
  public getActiveProfiles(): Array<{ profileIndex: number; slot: number }> {
    const result: Array<{ profileIndex: number; slot: number }> = []
    this.profileList.Profiles.forEach((profile, index) => {
      if (profile.slotIndex !== null) {
        result.push({ profileIndex: index, slot: profile.slotIndex })
      }
    })
    return result.sort((a, b) => a.slot - b.slot)
  }

  /**
   * 获取所有存档配置（slotIndex === null）
   */
  public getArchivedProfiles(): Array<{ profileIndex: number; name: string }> {
    return this.profileList.Profiles.map((profile, index) => ({ profileIndex: index, name: profile.Name, slot: profile.slotIndex }))
      .filter((item) => item.slot === null)
      .map(({ profileIndex, name }) => ({ profileIndex, name }))
  }

  /**
   * 将指定按键映射同步到所有其他板载绑定配置（不含当前配置）。
   * 用于火柴人设备固件自动同步切换键场景：
   * 本地其他槽位存档也需一并更新，防止切换槽位后 syncKeys() 写入旧数据覆盖固件同步结果。
   */
  public propagateKeysToOtherOnboardProfiles(changes: Array<{ kk: KK; isFn: boolean; output: Output }>): void {
    if (changes.length === 0) return
    const currentProfileIndex = this.profileList.CurrentProfileIndex
    let modified = false
    this.profileList.Profiles.forEach((profileMeta, index) => {
      if (index === currentProfileIndex) return
      if (profileMeta.slotIndex === null) return
      const dataProfile = profileMeta.ProfilesMap.get(deviceManager.deviceID)
      if (!dataProfile) return
      for (const { kk, isFn, output } of changes) {
        const existingIndex = dataProfile.Keys.findIndex((k) => k.Input.KK === kk && k.Input.WithFN === isFn)
        if (existingIndex > -1) {
          dataProfile.Keys.splice(existingIndex, 1)
        }
        dataProfile.Keys.push({
          Input: { KK: kk, WithFN: isFn, WithModifier: [] },
          Output: output,
        })
      }
      modified = true
    })
    if (modified) {
      this.save()
    }
  }

  /**
   * 将存档配置装入指定板载槽位，原槽位配置变为存档
   */
  public loadProfileIntoSlot(profileIndex: number, slot: number): number | null {
    if (profileIndex < 0 || profileIndex >= this.profileList.Profiles.length) {
      throw new Error('profile index out of range')
    }
    if (slot < 0 || slot > 3) {
      throw new Error('slot out of range (0-3)')
    }

    // 找到当前占据该槽位的配置
    const replacedIndex = this.getProfileIndexBySlot(slot)

    // 被替换的配置变为存档
    if (replacedIndex !== null && replacedIndex !== profileIndex) {
      this.profileList.Profiles[replacedIndex].slotIndex = null
    }

    // 清除新配置在其他槽位的旧绑定
    const currentSlot = this.profileList.Profiles[profileIndex].slotIndex
    if (currentSlot !== null) {
      this.profileList.Profiles[profileIndex].slotIndex = null
    }

    // 绑定到目标槽位
    this.profileList.Profiles[profileIndex].slotIndex = slot
    this.save()

    return replacedIndex !== profileIndex ? replacedIndex : null
  }

  public getProfileStateSnapshot() {
    return {
      CurrentProfileIndex: this.profileList.CurrentProfileIndex,
      slotIndexes: this.profileList.Profiles.map((profile) => profile.slotIndex ?? null),
    }
  }

  public restoreProfileStateSnapshot(snapshot: { CurrentProfileIndex: number; slotIndexes: Array<number | null> }) {
    if (snapshot.slotIndexes.length !== this.profileList.Profiles.length) {
      throw new Error('profile snapshot length mismatch')
    }

    this.profileList.Profiles.forEach((profile, index) => {
      profile.slotIndex = snapshot.slotIndexes[index] ?? null
    })
    this.profileList.CurrentProfileIndex = Math.min(Math.max(snapshot.CurrentProfileIndex, 0), this.profileList.Profiles.length - 1)

    const currentProfile = this.profileList.Profiles[this.profileList.CurrentProfileIndex]
    const dataProfile = currentProfile.ProfilesMap.get(deviceManager.deviceID)
    if (dataProfile) {
      this.dataProfile = dataProfile
      this.save()
      return
    }

    this.createDefaultProfile()
  }

  /**
   * 检查是否需要迁移（总配置数 > 4 且有未分配槽位的配置）。
   * 返回 true 表示需要弹出迁移选择器让用户手动选。
   */
  public needsMigration(): boolean {
    // 如果已经有 4 个槽位都分配了，说明迁移已完成
    const assignedSlots = new Set(this.profileList.Profiles.filter((p) => p.slotIndex !== null).map((p) => p.slotIndex))
    if (assignedSlots.size >= 4) return false
    // 总配置数 > 4 才需要用户选择
    return this.profileList.Profiles.length > 4
  }

  /**
   * 获取迁移选择器所需的配置列表信息
   */
  public getMigrationCandidates(): { index: number; name: string; isCurrent: boolean; slotIndex: number | null }[] {
    return this.profileList.Profiles.map((p, i) => ({
      index: i,
      name: p.Name,
      isCurrent: i === this.profileList.CurrentProfileIndex,
      slotIndex: p.slotIndex,
    }))
  }

  /**
   * 执行迁移：用户选定的 4 个配置分配到槽位 0-3，其余变为存档（slotIndex = null）。
   * @param keepIndices 用户选择的 4 个配置的 index（按槽位顺序）
   */
  public executeMigration(keepIndices: number[]) {
    if (keepIndices.length !== 4) throw new Error('must select exactly 4 configs')

    // 选中的配置分配槽位，未选中的标记为存档
    this.profileList.Profiles.forEach((profile, i) => {
      const slotOrder = keepIndices.indexOf(i)
      profile.slotIndex = slotOrder >= 0 ? slotOrder : null
    })

    this.save()
  }

  /**
   * 确保配置列表中有 4 个活跃槽位配置。
   * 当配置数 ≤ 4 时自动分配；> 4 时需先调用 needsMigration() + executeMigration()。
   * @returns 本次新分配的槽位索引数组（首次初始化时使用，用于标记待初始同步）
   */
  public ensureSlotConfigs(): number[] {
    // 如果需要迁移，不做自动处理，等待 UI 层调用 executeMigration
    if (this.needsMigration()) return []

    const newlyAssigned: number[] = []

    // 首次初始化：当前选中配置优先分配到 slot 0
    const hasBinding = this.profileList.Profiles.some((p) => p.slotIndex !== null)
    if (!hasBinding) {
      this.profileList.Profiles[this.profileList.CurrentProfileIndex].slotIndex = 0
      newlyAssigned.push(0)
    }

    for (let slot = 0; slot < 4; slot++) {
      const existing = this.getProfileIndexBySlot(slot)
      if (existing !== null) continue

      // 找一个没有槽位的配置分配给它
      const unassigned = this.profileList.Profiles.findIndex((p) => p.slotIndex === null)
      if (unassigned >= 0) {
        this.profileList.Profiles[unassigned].slotIndex = slot
        newlyAssigned.push(slot)
        continue
      }

      // 没有可用配置，创建新的
      this.profileList.Profiles.push({
        Name: `配置 ${slot + 1}`,
        slotIndex: slot,
        ProfilesMap: new Map<DefineDevice, DataProfile>(),
      })
      newlyAssigned.push(slot)
    }

    this.save()
    return newlyAssigned
  }

  // ─── 板载槽位首次同步标记 ───────────────────────────────────────────────────
  // 场景：固件从非板载版本升级到板载版本后，新槽位在设备端尚无用户数据，
  // 首次切换到该槽位时必须将本地配置完整写入设备（含 CMD 0x61 的 syncConfig）。

  private get pendingInitialSyncKey(): string {
    return `onboardPendingInitialSync_${this.getDeviceGroupKey()}`
  }

  private loadPendingInitialSync(): number[] {
    try {
      const raw = localStorage.getItem(this.pendingInitialSyncKey)
      if (!raw) return []
      const data = JSON.parse(raw)
      return Array.isArray(data) ? (data as number[]).filter((n) => typeof n === 'number') : []
    } catch {
      return []
    }
  }

  /**
   * 将指定槽位标记为"待首次同步"：首次切换到该槽位时必须将本地配置写入设备。
   */
  public markSlotsForInitialSync(slots: number[]) {
    const pending = new Set(this.loadPendingInitialSync())
    slots.forEach((s) => pending.add(s))
    localStorage.setItem(this.pendingInitialSyncKey, JSON.stringify([...pending]))
  }

  /**
   * 查询指定槽位是否处于"待首次同步"状态。
   */
  public isSlotPendingInitialSync(slot: number): boolean {
    return this.loadPendingInitialSync().includes(slot)
  }

  /**
   * 清除指定槽位的"待首次同步"标记（同步成功后调用）。
   */
  public clearSlotPendingInitialSync(slot: number) {
    const pending = this.loadPendingInitialSync().filter((s) => s !== slot)
    if (pending.length === 0) {
      localStorage.removeItem(this.pendingInitialSyncKey)
    } else {
      localStorage.setItem(this.pendingInitialSyncKey, JSON.stringify(pending))
    }
  }

  // ─── 板载首次整包同步完成标记 ───────────────────────────────────────────────
  // 记录该设备是否已经完成过"首次升级到板载固件后的初始同步"。
  // 仅存内存（onboardInitialSyncDoneSet），不写 localStorage：
  // 用户刷新网页后该标记清空，从而可以重新执行首次同步检测。
  // 设置后，本次会话内后续 handleOnboardCfgList 不再重复标记全部 slot 为 pending。

  public isOnboardInitialSyncDone(): boolean {
    return this.onboardInitialSyncDoneSet.has(this.getDeviceGroupKey())
  }

  // ─── 已同步固件版本 ─────────────────────────────────────────────────────────
  // 记录上次同步时设备的固件版本号。连接时与当前固件版本比对：
  // 版本一致且已同步过 → 数据无需重写；版本不一致（固件重刷/升级）→ 标记重同步。
  // 替代旧的「内容校验和 / magic cookie」重刷检测。

  private get syncedFirmwareVersionKey(): string {
    return `onboardSyncedFwVersion_${this.getDeviceGroupKey()}`
  }

  public getSyncedFirmwareVersion(): string | null {
    return localStorage.getItem(this.syncedFirmwareVersionKey)
  }

  public setSyncedFirmwareVersion(version: string) {
    localStorage.setItem(this.syncedFirmwareVersionKey, version)
  }

  public setOnboardInitialSyncDone() {
    this.onboardInitialSyncDoneSet.add(this.getDeviceGroupKey())
  }

  public getOnboardSlotDisplayName(slot: number) {
    const store = this.loadOnboardNameStore()
    const slotKey = String(slot)

    for (const deviceKey of this.getRelatedDeviceKeys()) {
      const name = store[deviceKey]?.[slotKey]
      if (typeof name === 'string' && name.trim().length > 0) {
        return name
      }
    }

    return null
  }

  public setOnboardSlotDisplayName(slot: number, name: string | null) {
    const store = this.loadOnboardNameStore()
    const slotKey = String(slot)
    const nextName = name?.trim() ?? ''

    this.getRelatedDeviceKeys().forEach((deviceKey) => {
      const deviceNames = { ...(store[deviceKey] || {}) }

      if (nextName.length > 0) {
        deviceNames[slotKey] = nextName
        store[deviceKey] = deviceNames
        return
      }

      delete deviceNames[slotKey]
      if (Object.keys(deviceNames).length > 0) {
        store[deviceKey] = deviceNames
      } else {
        delete store[deviceKey]
      }
    })

    this.saveOnboardNameStore(store)
  }

  /**
   * 将当前配置中的板载切换键(C1~C4)复制到所有其他已绑定板载槽位的配置。
   * 确保每个板载配置都有一致的切换键映射。
   */
  public replicateSwitchKeysToAllBoundProfiles() {
    const currentIndex = this.profileList.CurrentProfileIndex
    const currentProfile = this.profileList.Profiles[currentIndex]
    const currentData = currentProfile.ProfilesMap.get(deviceManager.deviceID)
    if (!currentData) return

    // 从当前配置提取所有切换键 KIO
    const switchVKs = new Set<VK>(onboardSwitchActionSlotMap.keys())
    const switchKeys = currentData.Keys.filter((kio) => switchVKs.has(kio.Output.VK as VK))

    // 遍历其他已绑定板载的配置，同步切换键
    this.profileList.Profiles.forEach((profile, index) => {
      if (index === currentIndex) return
      if (profile.slotIndex === null) return

      const targetData = profile.ProfilesMap.get(deviceManager.deviceID)
      if (!targetData) return

      // 移除目标配置中的旧切换键
      targetData.Keys = targetData.Keys.filter((kio) => !switchVKs.has(kio.Output.VK as VK))

      // 复制当前配置的切换键过去
      for (const switchKey of switchKeys) {
        targetData.Keys.push({
          Input: { ...switchKey.Input },
          Output: { ...switchKey.Output },
        } as KIO)
      }
    })

    this.save()
  }

  public ResetData() {
    this.createDefaultProfile()
  }

  public ResetKeys: KIO[] = []
  public ResetKmeKeys: KIO[] = []

  public clearKmeKeys() {
    this.ResetKmeKeys = []
  }

  public clearResetKeys() {
    if (this.ResetKeys.length < 1) {
      return
    }
    this.ResetKeys.forEach((value) => {
      if (value.Output.Mode == OutputMode.Macro) {
        this.dataProfile.Macros.delete(value.Output.DataIndex)
      }
      if (value.Output.Mode == OutputMode.Magnet) {
        this.dataProfile.MagnetData.delete(value.Output.DataIndex)
      }
      this.clearDataProfileKey(value)
    })
    this.ResetKeys = []
    this.save()
  }

  private clearDataProfileKey(value: KIO) {
    this.dataProfile.Keys = this.dataProfile.Keys.filter((value1) => value1.Input.KK != value.Input.KK || (value1.Input.KK == value.Input.KK && value.Input.WithFN != value1.Input.WithFN))
  }

  //导入配置
  public CfgImport(post: PostImportSetting) {
    this.assertCanAddProfile()
    const jsonString = atob(post.DataBase64)
    const value: DataProfile = JSON.parse(jsonString)
    const dp = {
      ...value,
      DeadZoneList: objectToMap(value.DeadZoneList),
      Items: objectToMap(value.Items),
      MagnetTravel: objectToMap(value.MagnetTravel),
      Macros: objectToMap(value.Macros),
      ColorsCustom: objectToMap(value.ColorsCustom),
      MagnetData: objectToMap(value.MagnetData),
      Decorates: value.Decorates
        ? value.Decorates.map((decorate: any) => ({
            ...decorate,
            ColorsCustom: objectToMap(decorate.ColorsCustom),
          }))
        : undefined,
    }
    const profileMetadata: ProfileMetadata = {
      Name: post.Name,
      slotIndex: null,
      ProfilesMap: new Map<DefineDevice, DataProfile>([[deviceManager.deviceID, dp]]),
    }
    this.profileList.Profiles.push(profileMetadata)
    this.save()

    return this.buildCfgCommonReturn()
  }

  //替换导入配置（配置已满时用，保留被替换配置的槽位绑定）
  public CfgImportReplace(post: PostImportReplace) {
    if (post.ReplaceIndex < 0 || post.ReplaceIndex >= this.profileList.Profiles.length) {
      throw new Error('replace index out of range')
    }
    if (post.ReplaceIndex === this.profileList.CurrentProfileIndex) {
      throw new Error('cannot replace current active profile')
    }

    const jsonString = atob(post.DataBase64)
    const value: DataProfile = JSON.parse(jsonString)
    const dp = {
      ...value,
      DeadZoneList: objectToMap(value.DeadZoneList),
      Items: objectToMap(value.Items),
      MagnetTravel: objectToMap(value.MagnetTravel),
      Macros: objectToMap(value.Macros),
      ColorsCustom: objectToMap(value.ColorsCustom),
      MagnetData: objectToMap(value.MagnetData),
      Decorates: value.Decorates
        ? value.Decorates.map((decorate: any) => ({
            ...decorate,
            ColorsCustom: objectToMap(decorate.ColorsCustom),
          }))
        : undefined,
    }

    // 保留原有的 slotIndex 和快捷键绑定，只替换名称和配置内容
    const existing = this.profileList.Profiles[post.ReplaceIndex]
    existing.Name = post.Name
    existing.ProfilesMap.set(deviceManager.deviceID, dp)

    this.save()
    return this.buildCfgCommonReturn()
  }

  // 导出配置
  CfgExport(post: PostInt): string {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    const dataProfile = this.profileList.Profiles[post.Value].ProfilesMap.get(deviceManager.deviceID)
    return btoa(JSON.stringify(dataProfile))
  }

  changeDpi(index: number) {
    this.dataProfile.Mouse.DpiIndex = index
    this.save()
  }

  changeMouseReportRate(value: number) {
    this.dataProfile.Mouse.Rate = value
    this.save()
  }
}

let _dataProfileManage: DataProfileManage | null = null

export const dataProfileManage = () => {
  if (_dataProfileManage == null) {
    _dataProfileManage = new DataProfileManage()
  }
  return _dataProfileManage
}
